import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useReads } from "../hooks/useReads";
import { getPurchase } from "../web3/contracts";
import { fmtUsdt, shortAddress, parseUsdt } from "../utils/format";
import { ACTIVE_CONTRACTS } from "../config/app.config";
import StatGrid from "../components/admin/StatGrid";
import ActionForm from "../components/admin/ActionForm";
import PauseControl from "../components/admin/PauseControl";
import RoleBanner from "../components/admin/RoleBanner";
import UserLookup from "../components/admin/UserLookup";
import AddressBar from "../components/admin/AddressBar";

const addr = (k, label, ph = "0x...") => ({ key: k, label, placeholder: ph });

export default function PurchaseAdmin() {
  const { signer } = useWeb3();
  const { data, reload } = useReads(async (provider) => {
    const p = getPurchase(provider);
    const [owner, admin, paused, reservePool, totalUsers, coreDex, project, router, t1, t2, usdt, scn, price] =
      await Promise.all([
        p.owner(), p.admin(), p.paused(), p.reservePool(), p.totalUsers(),
        p.coreDex(), p.smartcoreProject(), p.routerWallet(), p.teamWallet1(), p.teamWallet2(),
        p.usdt(), p.scn(), p.getCurrentSCNPrice(),
      ]);
    return { owner, admin, paused, reservePool, totalUsers, coreDex, project, router, t1, t2, usdt, scn, price };
  });

  const C = (s) => getPurchase(s);

  return (
    <div className="stack">
      <AddressBar label="Node Purchase Contract" address={ACTIVE_CONTRACTS.smartcorePurchase} />
      <RoleBanner owner={data?.owner} admin={data?.admin} />

      <StatGrid
        title="Node Purchase — Overview" icon="node" accent="violet" onRefresh={reload}
        rows={data ? [
          { label: "Owner", value: shortAddress(data.owner), mono: true },
          { label: "Admin", value: shortAddress(data.admin), mono: true },
          { label: "Paused", value: data.paused ? "Yes" : "No" },
          { label: "Total Users", value: data.totalUsers.toString() },
          { label: "Reserve Pool", value: `${fmtUsdt(data.reservePool)} USDT` },
          { label: "SCN Price", value: `$${fmtUsdt(data.price, 4)}` },
          { label: "CORE DEX", value: shortAddress(data.coreDex), mono: true },
          { label: "Project", value: shortAddress(data.project), mono: true },
          { label: "Router", value: shortAddress(data.router), mono: true },
          { label: "Team 1", value: shortAddress(data.t1), mono: true },
          { label: "Team 2", value: shortAddress(data.t2), mono: true },
          { label: "USDT", value: shortAddress(data.usdt), mono: true },
          { label: "SCN Token", value: shortAddress(data.scn), mono: true },
        ] : []}
      />

      <UserLookup />

      <PauseControl paused={data?.paused} getContract={C} signer={signer} onDone={reload} />

      <div className="adm-section-title">Admin user operations</div>
      <div className="adm-grid">
        <ActionForm title="Register User (free)" desc="No USDT, standard 2X ceiling." icon="users" accent="cyan"
          fields={[addr("a", "User address"), { key: "r", label: "Referrer ID", placeholder: "100000", numeric: true }]}
          button="Admin Register" onDone={reload}
          onRun={(v) => C(signer).adminRegister(v.a, ethers.BigNumber.from(v.r))} />

        <ActionForm title="Register Unlimited (VIP)" desc="Unlimited ceiling + all levels eligible." icon="shield" accent="gold"
          fields={[addr("a", "User address"), { key: "r", label: "Referrer ID", placeholder: "100000", numeric: true }]}
          button="Register VIP" onDone={reload}
          onRun={(v) => C(signer).adminRegisterUnlimited(v.a, ethers.BigNumber.from(v.r))} />

        <ActionForm title="Admin Upgrade" desc="Activate a higher package (2–9), no USDT." icon="layers" accent="violet"
          fields={[addr("a", "User address"), { key: "p", label: "Package ID (2–9)", placeholder: "2", numeric: true }]}
          button="Upgrade" onDone={reload}
          onRun={(v) => C(signer).adminUpgrade(v.a, ethers.BigNumber.from(v.p))} />

        <ActionForm title="Admin Re-topup" desc="Extend ceiling (1–9), no USDT." icon="spark" accent="cyan"
          fields={[addr("a", "User address"), { key: "p", label: "Package ID (1–9)", placeholder: "1", numeric: true }]}
          button="Re-topup" onDone={reload}
          onRun={(v) => C(signer).adminRetopup(v.a, ethers.BigNumber.from(v.p))} />
      </div>

      <div className="adm-section-title">Address configuration</div>
      <div className="adm-grid">
        <ActionForm title="Set CORE DEX" icon="layers" fields={[addr("a", "CORE DEX address")]} button="Set CORE DEX" onDone={reload} onRun={(v) => C(signer).setCoreDex(v.a)} />
        <ActionForm title="Set Project" icon="shield" fields={[addr("a", "Project address")]} button="Set Project" onDone={reload} onRun={(v) => C(signer).setSmartcoreProject(v.a)} />
        <ActionForm title="Set Router Wallet" icon="spark" fields={[addr("a", "Router address")]} button="Set Router" onDone={reload} onRun={(v) => C(signer).setRouterWallet(v.a)} />
        <ActionForm title="Set Team Wallet 1" icon="users" fields={[addr("a", "Team 1 address")]} button="Set Team 1" onDone={reload} onRun={(v) => C(signer).setTeamWallet1(v.a)} />
        <ActionForm title="Set Team Wallet 2" icon="users" fields={[addr("a", "Team 2 address")]} button="Set Team 2" onDone={reload} onRun={(v) => C(signer).setTeamWallet2(v.a)} />
        <ActionForm title="Set SCN Token (owner)" icon="coins" fields={[addr("a", "SCN token address")]} button="Set SCN" onDone={reload} onRun={(v) => C(signer).setScnToken(v.a)} />
        <ActionForm title="Set USDT (owner)" icon="coins" fields={[addr("a", "USDT address")]} button="Set USDT" onDone={reload} onRun={(v) => C(signer).setUSDT(v.a)} />
      </div>

      <div className="adm-section-title">Treasury & roles</div>
      <div className="adm-grid">
        <ActionForm title="Claim Reserve Pool" desc="Withdraw a USDT amount from reserve." icon="coins" accent="gold"
          fields={[{ key: "amt", label: "Amount (USDT)", placeholder: "100", numeric: true }]}
          button="Claim Reserve" onDone={reload}
          onRun={(v) => C(signer).claimReservePool(parseUsdt(v.amt))} />

        <ActionForm title="Claim Full Reserve" desc="Withdraw entire reserve pool." icon="coins" accent="gold"
          fields={[]} button="Claim Full Reserve" onDone={reload}
          onRun={() => C(signer).claimFullReservePool()} />

        <ActionForm title="Emergency Withdraw USDT" desc="Owner-only migration withdraw." icon="shield" danger
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (USDT)", placeholder: "100", numeric: true }]}
          button="Withdraw USDT" onDone={reload}
          onRun={(v) => C(signer).emergencyWithdrawUSDT(v.a, parseUsdt(v.amt))} />

        <ActionForm title="Set Admin (owner)" icon="shield" fields={[addr("a", "New admin address")]} button="Set Admin" onDone={reload} onRun={(v) => C(signer).setAdmin(v.a)} />

        <ActionForm title="Transfer Ownership" desc="Irreversible — double-check the address." icon="shield" danger
          fields={[addr("a", "New owner address")]} button="Transfer Ownership" onDone={reload}
          onRun={(v) => C(signer).transferOwnership(v.a)} />
      </div>
    </div>
  );
}
